/**
 * Figma "Manage access": someone's main role plus add-on services, where each role applies (the
 * whole business or chosen websites / domain folders), Identity's preview of the effect, then save
 * or remove.
 */
import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";

import { useOrganization } from "@/auth/organization";
import { Button, Field, Modal, Select, useToast } from "@/components/ui";
import {
  accessSignature,
  buildRestrictions,
  draftFrom,
  emptySelections,
  type ScopeDraft,
} from "@/lib/access";
import { missingPermissionReason } from "@/lib/access";
import { identity } from "@/lib/api";
import { formatDate } from "@/lib/format";
import type { Member, Role } from "@/lib/types";

import { AccessErrorAlert, AccessPreviewPanel, RoleScopes, useAccessResources } from "./AccessEditor";
import { useTeamRefresh } from "./dialogs";
import {
  ADD_ONS,
  canGrant,
  coveredBy,
  customerRoles,
  describeRole,
  firstName,
  shortName,
  splitRoles,
} from "./roles";
import { Avatar, CheckChip } from "./ui";

export function ManageMemberDialog({
  member,
  roles,
  self,
  onRemove,
  onClose,
}: {
  member: Member;
  roles: Role[];
  self: boolean;
  onRemove: () => void;
  onClose: () => void;
}) {
  const { orgId, can, permissions } = useOrganization();
  const refresh = useTeamRefresh();
  const all = customerRoles(roles);
  const byId = new Map(all.map((r) => [r.id, r]));
  const initial = splitRoles(member);
  const [mainId, setMainId] = useState(initial.main?.id ?? "");
  const [extras, setExtras] = useState(() => new Set(initial.extras.map((r) => r.id)));
  const [draft, setDraft] = useState<ScopeDraft>(() => draftFrom(member.roles));
  const main = byId.get(mainId);
  const first = firstName(member.user.display_name, member.user.email);
  const canEdit = can("members:manage_roles");
  const resources = useAccessResources(canEdit);

  // Your own roles can only go down, never up; for others, only roles you could grant yourself.
  const mainOptions = all.filter((r) => r.id === initial.main?.id || canGrant(r, permissions));
  const addOns = ADD_ONS.map((a) => ({ ...a, role: all.find((r) => r.key === a.key) })).filter(
    (a): a is typeof a & { role: Role } => !!a.role && a.role.id !== mainId && !coveredBy(a.role, main),
  );

  const wanted = [mainId, ...addOns.filter((a) => extras.has(a.role.id)).map((a) => a.role.id)].filter(
    Boolean,
  );
  const restrictions = buildRestrictions(wanted, draft, byId, resources.narrowable);
  const before = member.roles.map((r) => r.id);
  const initialRestrictions = buildRestrictions(before, draftFrom(member.roles), byId, resources.narrowable);
  const dirty = accessSignature(wanted, restrictions) !== accessSignature(before, initialRestrictions);
  const incomplete = emptySelections(restrictions);

  const toast = useToast();
  const save = useMutation({
    mutationFn: () => identity.setMemberRoles(orgId, member.user.id, wanted, restrictions),
    onSuccess: async () => {
      await refresh({ self });
      toast.show({ tone: "success", title: "Member access saved" });
      onClose();
    },
  });

  const grantNoteId = useId();

  return (
    <Modal
      open
      size="lg"
      onClose={onClose}
      title={self ? "Your access" : `Manage ${shortName(member.user.display_name, member.user.email)}`}
      footer={
        <div className="flex w-full flex-wrap items-center gap-2">
          <Button
            unstyled
            requires={self ? undefined : "members:remove"}
            onClick={onRemove}
            className="mr-auto rounded-lg px-2 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
          >
            {self ? "Leave business" : "Remove user"}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            requires="members:manage_roles"
            loading={save.isPending}
            disabledReason={
              !mainId
                ? "Choose a role."
                : incomplete
                  ? "Pick at least one website or domain folder for each narrowed role."
                  : !dirty
                    ? "Change something to save."
                    : undefined
            }
            onClick={() => save.mutate()}
          >
            Save access
          </Button>
        </div>
      }
    >
      <div className="flex items-center gap-3">
        <Avatar name={member.user.display_name || member.user.email} roleKey={initial.main?.key} size="lg" />
        <div className="min-w-0">
          <p className="truncate text-lg font-semibold text-fg">
            {shortName(member.user.display_name, member.user.email)}
          </p>
          <p className="truncate text-sm text-fg-subtle">
            {member.user.email} · member since {formatDate(member.joined_at)}
          </p>
        </div>
      </div>

      <AccessErrorAlert error={save.error} />

      {!canEdit && (
        <p className="text-sm text-fg-subtle">
          You can see this access but not change it. {missingPermissionReason("members:manage_roles")}
        </p>
      )}

      <Field label="Role" hint={main && describeRole(main)}>
        {(p) => (
          <Select
            {...p}
            value={mainId}
            disabled={!canEdit || mainOptions.length < 2}
            onChange={(e) => setMainId(e.target.value)}
          >
            {mainOptions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {canEdit && addOns.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-fg">
            Other services <span className="font-normal text-fg-subtle">Extra access on top of the role</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {addOns.map((a) => (
              <CheckChip
                key={a.key}
                checked={extras.has(a.role.id)}
                disabled={!canGrant(a.role, permissions) && !extras.has(a.role.id)}
                describedBy={grantNoteId}
                onChange={(on) =>
                  setExtras((prev) => {
                    const next = new Set(prev);
                    if (on) next.add(a.role.id);
                    else next.delete(a.role.id);
                    return next;
                  })
                }
              >
                {a.label}
              </CheckChip>
            ))}
          </div>
          <p id={grantNoteId} className="text-xs text-fg-subtle">
            Billing can only be granted by an Owner, and nobody can give more access than they have.
          </p>
        </div>
      )}

      {canEdit && (
        <RoleScopes
          roles={wanted.map((id) => byId.get(id)).filter((r): r is Role => !!r)}
          draft={draft}
          onChange={setDraft}
          resources={resources}
        />
      )}

      {canEdit && dirty && (
        <AccessPreviewPanel
          userId={member.user.id}
          roleIds={wanted}
          restrictions={restrictions}
          resources={resources}
          who={self ? "You" : first}
        />
      )}
    </Modal>
  );
}
